import { useCallback, useEffect } from 'react';

import { useRealtimeEvent, type UserEvent } from '@/features/realtime/RealtimeProvider';
import { useAuthStore } from '@/stores/authStore';

import { playChatPop, unlockChatSound } from '../lib/chatSound';

/**
 * App-root listener that pops a short sound when a chat message arrives from
 * the OTHER party.
 *
 * `chat.message` carries no sender id, only `senderRole` — which is enough to
 * never ping you for your own message: the backend fans your own send back to
 * you, so we key off the counterpart role instead. A student pings on
 * psychologist / system messages; a psychologist pings on student messages
 * (a colleague's reply is not a new client message, so it stays silent).
 *
 * Mounted next to the other chat listeners so a ping lands anywhere in the app,
 * not just on the chat screen.
 */
export function useChatMessageSound(): void {
  const role = useAuthStore((s) => s.user?.role);

  // Prime the AudioContext on the first real interaction — browsers keep it
  // suspended until then, which would silence the first inbound message.
  useEffect(() => {
    const unlock = () => {
      unlockChatSound();
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  const onMessage = useCallback(
    (event: Extract<UserEvent, { type: 'chat.message' }>) => {
      if (!role) return;
      const inbound =
        role === 'client' ? event.senderRole !== 'student' : event.senderRole === 'student';
      if (inbound) playChatPop();
    },
    [role],
  );
  useRealtimeEvent('chat.message', onMessage);
}
